<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Swiper from 'swiper'
import { Autoplay, Pagination } from 'swiper/modules'
import type { HomePageConfig } from '../../configs/types'
import BannerInlineCard from './BannerInlineCard.vue'
import { getBannerImage } from './bannerImage'
import { theme } from '@/config/brands'
import { useElementClick } from '@/composables/tracks'
import 'swiper/css'
import 'swiper/css/pagination'

const props = defineProps<{
  module: any
  config: HomePageConfig
  position: 'top' | 'inline'
  gotoBook: (id: string, firstChapterId?: string) => void
}>()

const emit = defineEmits<{
  (e: 'update:bg-url', url: string): void
}>()

const { elementClick } = useElementClick()
const primaryColor = theme.primaryColor

// 顶部 Banner 圆角
const topBannerRadius = computed(() => props.config.theme.topBannerRadius ?? '12px')
const topBannerBottomGradient = computed(() => props.config.theme.topBannerBottomGradient || '')
const hasBottomGradient = computed(() => !!topBannerBottomGradient.value)

function getBannerBg(record: any) {
  return getBannerImage(record)
}

const banners = computed(() => {
  const records: any[] = props.module?.resourceList?.records || []
  return records.filter(r => !!getBannerBg(r))
})

const swiper = ref<Swiper | null>(null)
const swiperRef = ref<HTMLElement | null>(null)
const paginationRef = ref<HTMLElement | null>(null)
const enableSwiper = computed(() => banners.value.length > 1)

function handleClick(record: any) {
  if (!record?.id)
    return
  elementClick(`banner_${props.module.id}_${record.id}`)
  props.gotoBook(record.id, record.firstChapter?.id)
}

function syncBgUrl(index: number) {
  const url = getBannerBg(banners.value[index])
  if (!url)
    return
  if (props.position === 'top')
    emit('update:bg-url', url)
}

function initSwiper() {
  if (!swiperRef.value || !enableSwiper.value)
    return
  swiper.value = new Swiper(swiperRef.value, {
    modules: [Pagination, Autoplay],
    grabCursor: true,
    centeredSlides: false,
    slidesPerView: 1,
    spaceBetween: 12,
    observer: true,
    observeParents: true,
    loop: true,
    autoplay: {
      delay: 4000,
      disableOnInteraction: false,
    },
    pagination: paginationRef.value
      ? {
          el: paginationRef.value,
          clickable: true,
        }
      : false,
    on: {
      init: (swiperInstance) => {
        syncBgUrl(swiperInstance.realIndex)
      },
      slideChange: (swiperInstance) => {
        syncBgUrl(swiperInstance.realIndex)
      },
      click(swiperInstance: any, event) {
        const clickedSlide = (event.target as Element)?.closest('.swiper-slide')
        if (!clickedSlide)
          return
        if (clickedSlide.classList.contains('swiper-slide-active'))
          handleClick(banners.value[swiperInstance.realIndex])
        else
          swiperInstance.slideToClickedSlide()
      },
    },
  })
}

watch(banners, () => {
  if (swiper.value) {
    swiper.value.destroy(true, true)
    swiper.value = null
  }
  nextTick(() => {
    initSwiper()
    syncBgUrl(0)
  })
})

onMounted(() => {
  if (enableSwiper.value)
    initSwiper()
  syncBgUrl(0)
})

onBeforeUnmount(() => {
  if (swiper.value) {
    swiper.value.destroy(true, true)
    swiper.value = null
  }
})
</script>

<template>
  <div v-if="banners.length" class="module-banner" :class="position">
    <!-- 顶部模式：纯 backgroundImageUrl 大图轮播 -->
    <template v-if="position === 'top'">
      <!-- 单条：不走 Swiper -->
      <div
        v-if="!enableSwiper"
        class="banner-top-single"
        @click="handleClick(banners[0])"
      >
        <div class="banner-top-img" :style="{ backgroundImage: `url(${getBannerBg(banners[0])})` }" />
        <div v-if="hasBottomGradient" class="banner-bottom-mask" />
      </div>

      <!-- 多条：Swiper -->
      <div v-else class="banner-top-wrap">
        <div ref="swiperRef" class="banner-top swiper">
          <div class="swiper-wrapper">
            <div
              v-for="(record, index) in banners"
              :key="record.id || index"
              class="swiper-slide"
            >
              <div class="banner-top-img" :style="{ backgroundImage: `url(${getBannerBg(record)})` }" />
            </div>
          </div>
          <div ref="paginationRef" class="swiper-pagination" />
        </div>
        <div v-if="hasBottomGradient" class="banner-bottom-mask" />
      </div>
    </template>

    <!-- 非顶部模式：左书封 + 右描述卡片 -->
    <template v-else>
      <!-- 单条：不走 Swiper -->
      <div v-if="!enableSwiper" class="banner-inline-click" @click="handleClick(banners[0])">
        <BannerInlineCard :record="banners[0]" :module="module" />
      </div>

      <!-- 多条：Swiper -->
      <div v-else class="banner-inline-wrap">
        <div ref="swiperRef" class="banner-inline swiper">
          <div class="swiper-wrapper">
            <div
              v-for="(record, index) in banners"
              :key="record.id || index"
              class="swiper-slide banner-inline-click"
            >
              <BannerInlineCard :record="record" :module="module" />
            </div>
          </div>
          <div ref="paginationRef" class="swiper-pagination" />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.module-banner {
  width: 100%;
}

/* ---------- 顶部模式：大背景轮播 ---------- */
.banner-top-single,
.banner-top-wrap {
  width: 100%;
  aspect-ratio: 750 / 830;
  position: relative;
  overflow: hidden;
  border-radius: v-bind(topBannerRadius);
}

.banner-top {
  width: 100%;
  height: 100%;
}

.banner-top :deep(.swiper-wrapper),
.banner-top :deep(.swiper-slide) {
  width: 100%;
  height: 100%;
}

.banner-top-img {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
}

/* 底部渐变蒙层：使各品牌可在 home config 中配置 topBannerBottomGradient 启用 */
.banner-bottom-mask {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 80px;
  pointer-events: none;
  z-index: 5;
  background: v-bind(topBannerBottomGradient);
}

.banner-top :deep(.swiper-pagination) {
  position: absolute;
  bottom: 14px;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.banner-top :deep(.swiper-pagination-bullet) {
  background: #ffffff;
  opacity: 0.5;
}

.banner-top :deep(.swiper-pagination-bullet-active) {
  background: #ffffff;
  opacity: 1;
}

/* ---------- 非顶部模式 ---------- */
.banner-inline-wrap {
  position: relative;
  width: 100%;
}

.banner-inline {
  position: relative;
  width: 100%;
  height: 121px;
}

.banner-inline :deep(.swiper-wrapper) {
  height: 121px;
}

.banner-inline :deep(.swiper-slide) {
  width: 100%;
  height: 121px;
  box-sizing: border-box;
}

.banner-inline-click {
  cursor: pointer;
}

/* ---------- 顶部模式分页器 bullet（白色，叠在大图上） ---------- */
:deep(.swiper-pagination-bullet) {
  width: 6px;
  height: 6px;
  background: #ffffff;
  border-radius: 3px;
  opacity: 0.5;
  margin: 0 4px;
}

:deep(.swiper-pagination-bullet-active) {
  width: 20px;
  height: 6px;
  background: v-bind(primaryColor);
  border-radius: 3px;
  opacity: 1;
}

/* 非顶部分页器：卡片内底部居中，尺寸更小 */
.banner-inline :deep(.swiper-pagination) {
  position: absolute;
  bottom: 8px;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.banner-inline :deep(.swiper-pagination-bullet) {
  width: 5px;
  height: 5px;
  background: #ffffff;
  border-radius: 3px;
  opacity: 0.5;
  margin: 0 3px;
}

.banner-inline :deep(.swiper-pagination-bullet-active) {
  width: 14px;
  height: 5px;
  background: v-bind(primaryColor);
  border-radius: 3px;
  opacity: 1;
}
</style>
